import { lookup } from '../runtime.js';
import { formatNumber } from './number.js';
/**
 * Mapping DB UoM code → canonical i18n code.
 *
 * В проде `bom_items.uom` / `items.uom` содержат смешанные значения:
 * `meter`, `Piece`, `kg`, `кг`, `литр`, `шт`, `mm`, `Kg`, и т.д.
 * `normaliseUomCode` приводит их к canonical ключу (`m`, `pcs`, `kg`, ...),
 * под который есть `units_{code}_short` / `units_{code}_long` в messages.
 *
 * При отсутствии маппинга — identity (возврат raw). `formatQty` далее
 * делает graceful fallback на raw если `units_*_short` тоже нет.
 */
const UOM_ALIAS_MAP = {
    // metre / meter
    meter: 'm',
    metre: 'm',
    м: 'm',
    m: 'm',
    // millimeter
    mm: 'mm',
    мм: 'mm',
    millimeter: 'mm',
    millimetre: 'mm',
    // centimeter
    cm: 'cm',
    см: 'cm',
    // square / cubic
    m2: 'm2',
    м2: 'm2',
    'м²': 'm2',
    'sq meter': 'm2',
    'sq metre': 'm2',
    // FE-65 sweep: imperial area — render as «фут²» (no conversion, keep source UoM precision).
    ft2: 'ft2',
    'ft²': 'ft2',
    sqft: 'ft2',
    'sq ft': 'ft2',
    m3: 'm3',
    м3: 'm3',
    'м³': 'm3',
    'cu meter': 'm3',
    'cu metre': 'm3',
    // piece / unit
    piece: 'pcs',
    pc: 'pcs',
    pcs: 'pcs',
    nos: 'pcs',
    unit: 'pcs',
    each: 'pcs',
    шт: 'pcs',
    штука: 'pcs',
    штуки: 'pcs',
    штук: 'pcs',
    // mass
    kg: 'kg',
    kilogram: 'kg',
    кг: 'kg',
    килограмм: 'kg',
    g: 'g',
    gram: 'g',
    г: 'g',
    грамм: 'g',
    t: 't',
    ton: 't',
    tonne: 't',
    т: 't',
    тонна: 't',
    // volume
    l: 'L',
    liter: 'L',
    litre: 'L',
    литр: 'L',
    ml: 'ml',
    миллилитр: 'ml',
    // time
    h: 'h',
    hour: 'h',
    ч: 'h',
    час: 'h',
    min: 'min',
    minute: 'min',
    мин: 'min',
    минута: 'min',
    // packaging
    pkg: 'pkg',
    package: 'pkg',
    pack: 'pkg',
    упак: 'pkg',
    упаковка: 'pkg',
    set: 'set',
    компл: 'set',
    комплект: 'set',
    roll: 'roll',
    рул: 'roll',
    рулон: 'roll',
};
export function normaliseUomCode(raw) {
    const k = raw.trim().toLowerCase();
    return UOM_ALIAS_MAP[k] ?? raw;
}
/**
 * Formats quantity + localized UoM short label.
 *  - `formatQty('1453.50', 'm2', 'ru')`   → "1 453,5 м²"
 *  - `formatQty('14535000', 'mm', 'ru')`  → "14 535 000 мм"
 *  - `formatQty('0.04', 'Piece', 'ru')`   → "0,04 шт"
 *  - `formatQty('14535000', null, 'ru')`  → "14 535 000"
 */
export function formatQty(value, uomCode, locale) {
    if (value === null || value === undefined || value === '')
        return '—';
    const num = formatNumber(value, locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
    if (num === '—')
        return '—';
    if (!uomCode)
        return num;
    const canonical = normaliseUomCode(uomCode);
    const unit = lookup(`units_${canonical}_short`, locale);
    return `${num} ${unit.startsWith('[missing') ? uomCode : unit}`;
}
//# sourceMappingURL=qty.js.map